import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ClaimsList from '../components/admin/ClaimsList';
import { toast } from 'react-hot-toast';
import userService from '../services/userService';

const AdminDashboardPage = () => {
    const { user, logout } = useAuth();
    const [activeTab, setActiveTab] = useState('claims');
    const [pendingClaimsCount, setPendingClaimsCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    
    useEffect(() => {
        // Check if user has admin/manager role
        const isAdmin = user?.role === 'admin' || user?.role_name === 'admin';
        const isManager = user?.role === 'manager' || user?.role_name === 'manager';
        
        if (!isAdmin && !isManager) {
            toast.error('You do not have permission to access the admin dashboard');
            navigate('/');
        }
    }, [user, navigate]);

    // Fetch pending claims count
    useEffect(() => {
        const fetchPendingClaimsCount = async () => {
            try {
                setLoading(true);
                const pendingClaims = await userService.getPendingClaims();
                setPendingClaimsCount(pendingClaims.length);
            } catch (error) {
                console.error('Error fetching pending claims:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchPendingClaimsCount();
    }, []);

    const handleLogout = () => {
        logout();
    };

    // Handle tab switch
    const handleTabChange = (tabKey) => {
        setActiveTab(tabKey);
    };

    const tabs = [
        { key: 'overview', label: 'Overview' },
        { key: 'claims', label: 'Claims', count: pendingClaimsCount },
    ];

    return (
        <div className="admin-dashboard-page bg-gray-100 min-h-screen">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="py-10">
                    <div className="mb-8">
                        <div className="flex justify-between items-center">
                            <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
                            <button
                                onClick={handleLogout}
                                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            >
                                Sign out
                            </button>
                        </div>
                        <p className="mt-2 text-sm text-gray-600">
                            Logged in as {user?.email} ({user?.role})
                        </p>
                    </div>

                    {/* Tab Navigation */}
                    <div className="mb-8">
                        <div className="border-b border-gray-200">
                            <div className="flex flex-wrap -mb-px">
                                {tabs.map((tab) => (
                                    <button
                                        key={tab.key}
                                        className={`inline-block py-4 px-6 text-sm font-medium border-b-2 ${
                                            activeTab === tab.key
                                                ? 'border-pink-500 text-pink-600'
                                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                        } flex items-center`}
                                        onClick={() => handleTabChange(tab.key)}
                                    >
                                        {tab.label}
                                        {tab.count > 0 && (
                                            <span className={`ml-2 px-2 py-0.5 text-xs font-medium rounded-full ${
                                                activeTab === tab.key 
                                                    ? 'bg-pink-100 text-pink-800' 
                                                    : 'bg-gray-100 text-gray-800'
                                            }`}>
                                                {tab.count}
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Tab Content */}
                    {activeTab === 'overview' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {/* SEO Management Card */}
                            <div className="bg-white overflow-hidden shadow rounded-lg">
                                <div className="p-5">
                                    <div className="flex items-center">
                                        <div className="flex-shrink-0 bg-pink-500 rounded-md p-3">
                                            <svg className="h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                                            </svg>
                                        </div>
                                        <div className="ml-5 w-0 flex-1">
                                            <dl>
                                                <dt className="text-sm font-medium text-gray-500 truncate">
                                                    SEO Management
                                                </dt>
                                                <dd>
                                                    <div className="text-lg font-medium text-gray-900">
                                                        Manage meta tags
                                                    </div>
                                                </dd>
                                            </dl>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-gray-50 px-5 py-3">
                                    <div className="text-sm">
                                        <Link to="/admin/seo" className="font-medium text-pink-700 hover:text-pink-900">
                                            Manage SEO
                                        </Link>
                                    </div>
                                </div>
                            </div>

                            {/* SRA Sync Card */}
                            <div className="bg-white overflow-hidden shadow rounded-lg">
                                <div className="p-5">
                                    <div className="flex items-center">
                                        <div className="flex-shrink-0 bg-pink-500 rounded-md p-3">
                                            <svg className="h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                            </svg>
                                        </div>
                                        <div className="ml-5 w-0 flex-1">
                                            <dl>
                                                <dt className="text-sm font-medium text-gray-500 truncate">
                                                    SRA Directory Sync
                                                </dt>
                                                <dd>
                                                    <div className="text-lg font-medium text-gray-900">
                                                        Import latest data
                                                    </div>
                                                </dd>
                                            </dl>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-gray-50 px-5 py-3">
                                    <div className="text-sm">
                                        <Link to="/admin/sra-sync" className="font-medium text-pink-700 hover:text-pink-900">
                                            Run SRA Sync
                                        </Link>
                                    </div>
                                </div>
                            </div>

                            {/* Claims Card */}
                            <div className="bg-white overflow-hidden shadow rounded-lg">
                                <div className="p-5">
                                    <div className="flex items-center">
                                        <div className="flex-shrink-0 bg-pink-500 rounded-md p-3">
                                            <svg className="h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </div>
                                        <div className="ml-5 w-0 flex-1">
                                            <dl>
                                                <dt className="text-sm font-medium text-gray-500 truncate">
                                                    Pending Claims
                                                </dt>
                                                <dd>
                                                    <div className="text-lg font-medium text-gray-900">
                                                        {loading ? 
                                                            "Loading..." : 
                                                            `${pendingClaimsCount} verification requests`
                                                        }
                                                    </div>
                                                </dd>
                                            </dl>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-gray-50 px-5 py-3">
                                    <div className="text-sm">
                                        <button 
                                            onClick={() => handleTabChange('claims')} 
                                            className="font-medium text-pink-700 hover:text-pink-900"
                                        >
                                            Review Claims
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Users Card */}
                            <div className="bg-white overflow-hidden shadow rounded-lg">
                                <div className="p-5">
                                    <div className="flex items-center">
                                        <div className="flex-shrink-0 bg-pink-500 rounded-md p-3">
                                            <svg className="h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                            </svg>
                                        </div>
                                        <div className="ml-5 w-0 flex-1">
                                            <dl>
                                                <dt className="text-sm font-medium text-gray-500 truncate">
                                                    User Management
                                                </dt>
                                                <dd>
                                                    <div className="text-lg font-medium text-gray-900">
                                                        Manage users
                                                    </div>
                                                </dd>
                                            </dl>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-gray-50 px-5 py-3">
                                    <div className="text-sm">
                                        <Link to="/admin/users" className="font-medium text-pink-700 hover:text-pink-900">
                                            Manage Users
                                        </Link>
                                    </div>
                                </div>
                            </div>

                            {/* Appointments Management Card */}
                            <div className="bg-white overflow-hidden shadow rounded-lg">
                                <div className="p-5">
                                    <div className="flex items-center">
                                        <div className="flex-shrink-0 bg-indigo-500 rounded-md p-3">
                                            <svg className="h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                        </div>
                                        <div className="ml-5 w-0 flex-1">
                                            <dl>
                                                <dt className="text-sm font-medium text-gray-500 truncate">
                                                    Appointments & Enquiries
                                                </dt>
                                                <dd className="text-lg font-medium text-gray-900">
                                                    View All Bookings
                                                </dd>
                                            </dl>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-gray-50 px-5 py-3">
                                    <div className="text-sm">
                                        <Link to="/admin/appointments" className="font-medium text-cyan-700 hover:text-cyan-900">
                                            Manage appointments and enquiries →
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'claims' && (
                        <ClaimsList />
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminDashboardPage; 